import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { calculatePages } from './helpers/calculatePages';
import styles from './pagination.module.scss';

const MAX_MOBILE_OPTIONS = 3;
const MAX_LARGE_OPTIONS = 9;

// Call the method `calculatePages` to get the right amount and order of the pages
// to display and creates an array of `LI` tags with the page number.
const RenderPages = ({ currentPage, maxPages, handlePageSelected, isMobile, disabled }) => {
  const maxOptions = isMobile ? MAX_MOBILE_OPTIONS : MAX_LARGE_OPTIONS;
  const pages = calculatePages(currentPage, maxPages, maxOptions);
  return pages.map((page) => (
    <li
      key={page}
      className={classNames({ [styles.active]: currentPage === page, [styles.disabled]: disabled })}
    >
      <button onClick={() => handlePageSelected(page)} type='button'>
        {page}
      </button>
    </li>
  ));
};

RenderPages.propTypes = {
  currentPage: PropTypes.number,
  maxPages: PropTypes.number,
  handlePageSelected: PropTypes.func,
  isMobile: PropTypes.bool,
  disabled: PropTypes.bool,
};

const Pagination = ({
  currentPage = 1,
  maxPages = 31,
  disabled,
  handlePageSelected,
  ...rest
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 600);

    const handleOrientation = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('orientationchange', handleOrientation);

    return () => {
      window.removeEventListener('orientationchange', handleOrientation);
    };
  }, []);

  const firstDisplay = (
    <li className={classNames({ [styles.disabled]: disabled || currentPage === 1 })}>
      <button
        onClick={() => handlePageSelected(1)}
        className={styles.selectors}
        type='button'
      >
        First
      </button>
    </li>
  );

  const prevDisplay = (
    <li className={classNames({ [styles.disabled]: disabled || currentPage <= 1 })}>
      <button
        onClick={() => handlePageSelected(currentPage - 1)}
        className={styles.selectors}
        type='button'
      >
        Prev
      </button>
    </li>
  );

  const nextDisplay = (
    <li className={classNames({ [styles.disabled]: disabled || currentPage + 1 > maxPages })}>
      <button
        onClick={() => handlePageSelected(currentPage + 1)}
        className={styles.selectors}
        type='button'
      >
        Next
      </button>
    </li>
  );

  const lastDisplay = (
    <li className={classNames({ [styles.disabled]: disabled || currentPage === maxPages })}>
      <button
        onClick={() => handlePageSelected(maxPages)}
        className={styles.selectors}
        type='button'
      >
        Last
      </button>
    </li>
  );

  return (
    <div className={styles.paginationContainer} data-testid='react-pagination'>
      <ul className={styles.pagination} aria-label='pagination'>
        {firstDisplay}
        {prevDisplay}
        <RenderPages
          {...rest}
          currentPage={currentPage}
          maxPages={maxPages}
          handlePageSelected={handlePageSelected}
          isMobile={isMobile}
          disabled={disabled}
        />
        {nextDisplay}
        {lastDisplay}
      </ul>
    </div>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number,
  maxPages: PropTypes.number,
  handlePageSelected: PropTypes.func,
  disabled: PropTypes.bool,
};

export default Pagination;
